/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 *
 * Centralized shortcuts configuration for DriveViewer
 */

import {
  Shortcut,
  ShortcutKey,
  ShortcutAddonKey,
  ShortcutCategory,
} from '@ouster/webviz-react-components';

/**
 * Interface for shortcut handler callbacks
 */
export interface ShortcutHandlers {
  // Playbar
  togglePause: () => void;
  increasePlaybackSpeed: () => void;
  decreasePlaybackSpeed: () => void;
  previousFrame: () => void;
  nextFrame: () => void;
  rewind10Frames: () => void;
  fastForward10Frames: () => void;

  // 2D Image
  toggle2DImageVisibility: () => void;
  // cycleImageColorMode: () => void;
  // cycleImageColorPalette: () => void;
  // toggleImageAutoLevel: () => void;
  // toggleNearIRNoiseSuppression: () => void;

  // Point Cloud
  incrementPointSize: () => void;
  decrementPointSize: () => void;
  // cyclePointReturnsCombination: () => void;
  // cyclePointColorMode: () => void;
  // cyclePointColorPalette: () => void;
  togglePointCloudAutoLevel: () => void;
  // toggleRangeRingsVisibility: () => void;

  // Camera
  toggleCameraOrthographicMode: () => void;
  // setCameraPresetTopdown: () => void;
  // cycleCameraPresets: () => void;

  // Other
  toggleLeftSidebar: () => void;
}

/**
 * Creates all registered shortcuts for DriveViewer
 * @param handlers Object containing all shortcut handler callbacks
 * @returns Array of Shortcut objects
 */
export const createDriveViewerShortcuts = (
  handlers: ShortcutHandlers
): Shortcut[] => {
  const shortcuts: Shortcut[] = [];

  // =========================================================================
  // PLAYBAR SHORTCUTS - ACTIVE
  // =========================================================================
  shortcuts.push(
    new Shortcut(
      ShortcutCategory.Playbar,
      [new ShortcutKey(' ', [], 'Space')],
      () => handlers.togglePause(),
      'Play/Pause Replay'
    )
  );

  shortcuts.push(
    new Shortcut(
      ShortcutCategory.Playbar,
      [new ShortcutKey('>', [ShortcutAddonKey.SHIFT])],
      () => handlers.increasePlaybackSpeed(),
      'Increase Playback Speed'
    )
  );

  shortcuts.push(
    new Shortcut(
      ShortcutCategory.Playbar,
      [new ShortcutKey('<', [ShortcutAddonKey.SHIFT])],
      () => handlers.decreasePlaybackSpeed(),
      'Decrease Playback Speed'
    )
  );

  shortcuts.push(
    new Shortcut(
      ShortcutCategory.Playbar,
      [new ShortcutKey('ArrowLeft', [], '\u2190'), new ShortcutKey(',')],
      () => handlers.previousFrame(),
      'Previous Frame'
    )
  );

  shortcuts.push(
    new Shortcut(
      ShortcutCategory.Playbar,
      [new ShortcutKey('ArrowRight', [], '\u2192'), new ShortcutKey('.')],
      () => handlers.nextFrame(),
      'Next Frame'
    )
  );

  shortcuts.push(
    new Shortcut(
      ShortcutCategory.Playbar,
      [new ShortcutKey('ArrowLeft', [ShortcutAddonKey.SHIFT], '\u2190')],
      () => handlers.rewind10Frames(),
      'Back 10 Frames'
    )
  );

  shortcuts.push(
    new Shortcut(
      ShortcutCategory.Playbar,
      [new ShortcutKey('ArrowRight', [ShortcutAddonKey.SHIFT], '\u2192')],
      () => handlers.fastForward10Frames(),
      'Forward 10 Frames'
    )
  );

  // =========================================================================
  // 2D IMAGE SHORTCUTS - ACTIVE
  // =========================================================================
  shortcuts.push(
    new Shortcut(
      ShortcutCategory.TwoDImage,
      [new ShortcutKey('i')],
      () => handlers.toggle2DImageVisibility(),
      'Toggle 2D Image'
    )
  );

  // =========================================================================
  // 2D IMAGE SHORTCUTS - COMMENTED (Future implementation)
  // =========================================================================
  // shortcuts.push(
  //   new Shortcut(
  //     ShortcutCategory.TwoDImage,
  //     [new ShortcutKey('z')],
  //     () => handlers.cycleImageReturnsCombination(),
  //     'Cycle through Returns'
  //   )
  // );

  // shortcuts.push(
  //   new Shortcut(
  //     ShortcutCategory.TwoDImage,
  //     [new ShortcutKey('r')],
  //     () => handlers.cycleImageColorMode(),
  //     'Change Coloring Mode'
  //   )
  // );

  // shortcuts.push(
  //   new Shortcut(
  //     ShortcutCategory.TwoDImage,
  //     [new ShortcutKey('d')],
  //     () => handlers.cycleImageColorPalette(),
  //     'Change Color Palette'
  //   )
  // );

  // shortcuts.push(
  //   new Shortcut(
  //     ShortcutCategory.TwoDImage,
  //     [new ShortcutKey('A', [ShortcutAddonKey.SHIFT])],
  //     () => {
  //       try {
  //         const useLevels = handlers.toggleImageAutoLevel();
  //         // launchNewToast(`Image auto-levels: ${useLevels ? 'ON' : 'OFF'}`);
  //       } catch (error) {
  //         // if (error instanceof Error) {
  //         //   launchNewToast(`${error.message}`);
  //         // } else {
  //         //   launchNewToast(`${error}`);
  //         // }
  //       }
  //     },
  //     'Toggle Auto-Levels'
  //   )
  // );

  // shortcuts.push(
  //   new Shortcut(
  //     ShortcutCategory.TwoDImage,
  //     [new ShortcutKey('S', [ShortcutAddonKey.SHIFT])],
  //     () => {
  //       try {
  //         const suppressNoise = handlers.toggleNearIRNoiseSuppression();
  //         // launchNewToast(`Noise Suppression: ${suppressNoise ? 'ON' : 'OFF'}`);
  //       } catch (error) {
  //         // if (error instanceof Error) {
  //         //   launchNewToast(`${error.message}`);
  //         // } else {
  //         //   launchNewToast(`${error}`);
  //         // }
  //       }
  //     },
  //     'Toggle Near Infrared Noise Suppression'
  //   )
  // );

  // =========================================================================
  // POINT CLOUD SHORTCUTS - ACTIVE
  // =========================================================================
  shortcuts.push(
    new Shortcut(
      ShortcutCategory.PointCloud,
      [new ShortcutKey('p')],
      () => handlers.incrementPointSize(),
      'Increase Point Size'
    )
  );

  shortcuts.push(
    new Shortcut(
      ShortcutCategory.PointCloud,
      [new ShortcutKey('P', [ShortcutAddonKey.SHIFT])],
      () => handlers.decrementPointSize(),
      'Decrease Point Size'
    )
  );

  shortcuts.push(
    new Shortcut(
      ShortcutCategory.PointCloud,
      [new ShortcutKey('v')],
      () => {
        try {
          handlers.togglePointCloudAutoLevel();
        } catch (error) {
          // Error handling done in component if needed
        }
      },
      'Toggle Autolevels'
    )
  );

  // =========================================================================
  // POINT CLOUD SHORTCUTS - COMMENTED (Future implementation)
  // =========================================================================
  // shortcuts.push(
  //   new Shortcut(
  //     ShortcutCategory.PointCloud,
  //     [new ShortcutKey('z')],
  //     () => {
  //       try {
  //         const newCombination = handlers.cyclePointReturnsCombination();
  //         // launchNewToast message with combination info
  //       } catch (error) {
  //         // Error handling
  //       }
  //     },
  //     'Cycle through Returns'
  //   )
  // );

  // shortcuts.push(
  //   new Shortcut(
  //     ShortcutCategory.PointCloud,
  //     [new ShortcutKey('g')],
  //     () => handlers.cyclePointColorMode(),
  //     'Change Coloring Mode'
  //   )
  // );

  // shortcuts.push(
  //   new Shortcut(
  //     ShortcutCategory.PointCloud,
  //     [new ShortcutKey('f')],
  //     () => handlers.cyclePointColorPalette(),
  //     'Change Color Palette'
  //   )
  // );

  // shortcuts.push(
  //   new Shortcut(
  //     ShortcutCategory.PointCloud,
  //     [new ShortcutKey('p')],
  //     () => handlers.cyclePointShape(),
  //     'Change Point Shape'
  //   )
  // );

  // shortcuts.push(
  //   new Shortcut(
  //     ShortcutCategory.PointCloud,
  //     [new ShortcutKey('x')],
  //     () => handlers.toggleRangeRingsVisibility(),
  //     'Toggle Range Rings'
  //   )
  // );

  // =========================================================================
  // CAMERA SHORTCUTS - ACTIVE
  // =========================================================================
  shortcuts.push(
    new Shortcut(
      ShortcutCategory.Camera,
      [new ShortcutKey('C', [ShortcutAddonKey.SHIFT])],
      () => handlers.toggleCameraOrthographicMode(),
      'Toggle Ortho Camera'
    )
  );
  // =========================================================================
  // OTHER SHORTCUTS
  // =========================================================================
  shortcuts.push(
    new Shortcut(
      ShortcutCategory.Other,
      [new ShortcutKey('ArrowLeft', [ShortcutAddonKey.ALT], '\u2190')],
      () => handlers.toggleLeftSidebar(),
      'Show/Hide Left Sidebar'
    )
  );

  // =========================================================================
  // CAMERA SHORTCUTS - COMMENTED (Future implementation)
  // =========================================================================
  // shortcuts.push(
  //   new Shortcut(
  //     ShortcutCategory.Camera,
  //     [new ShortcutKey('a')],
  //     () => {
  //       handlers.setCameraPresetTopdown();
  //       // launchNewToast('Camera view: Top');
  //     },
  //     'Topdown View'
  //   )
  // );

  // shortcuts.push(
  //   new Shortcut(
  //     ShortcutCategory.Camera,
  //     [new ShortcutKey('s')],
  //     () => {
  //       const newCameraPreset = handlers.cycleCameraPresets();
  //       // launchNewToast(`Camera view: ${newCameraPreset.name}`);
  //     },
  //     'Cycle through Views'
  //   )
  // );

  // shortcuts.push(
  //   new Shortcut(
  //     ShortcutCategory.Camera,
  //     [new ShortcutKey('=')],
  //     () => handlers.zoomIn(),
  //     'Zoom In'
  //   )
  // );

  // shortcuts.push(
  //   new Shortcut(
  //     ShortcutCategory.Camera,
  //     [new ShortcutKey('-')],
  //     () => handlers.zoomOut(),
  //     'Zoom Out'
  //   )
  // );

  return shortcuts;
};

/**
 * Gets a summary of all registered shortcuts
 * Useful for debugging and documentation
 */
export const getShortcutsSummary = (shortcuts: Shortcut[]): string => {
  const summary = shortcuts
    .map((s) => {
      // ShortcutKey has key property, and optionally a display name
      const keys = s.keys
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
        .map((k: any) => k.display || k.key || 'Unknown')
        .join(' or ');
      return `[${s.category}] ${s.description}: ${keys}`;
    })
    .join('\n');

  return summary;
};

/**
 * Gets shortcuts grouped by category
 */
export const getShortcutsGroupedByCategory = (
  shortcuts: Shortcut[]
): Record<string, Shortcut[]> =>
  shortcuts.reduce((acc, shortcut) => {
    if (!acc[shortcut.category]) {
      acc[shortcut.category] = [];
    }
    acc[shortcut.category].push(shortcut);
    return acc;
  }, {} as Record<string, Shortcut[]>);
